import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import { LovDTO } from '@api/ringil-api.v2';
import { MultiSelect } from 'primereact/multiselect';
import { Skeleton } from 'primereact/skeleton';

interface ComponentProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  drivers: LovDTO[];
  loading: boolean;
  onLazyLoad: () => void;
}

export const InputDrivers: React.FC<ComponentProps> = ({ value, onChange, placeholder, drivers, loading, onLazyLoad }) => {
  const allOptions = React.useMemo(() => {
    const existingIds = new Set(drivers.map(d => d.id));
    const missingSelected = value
      .filter(id => !existingIds.has(id))
      .map(id => ({ id, name: id } as LovDTO));
    return [...drivers, ...missingSelected];
  }, [drivers, value]);

  return (
    <MultiSelect
      options={allOptions}
      optionLabel={'name'}
      optionValue={'id'}
      value={value}
      onChange={e => onChange(e.value)}
      placeholder={placeholder}
      virtualScrollerOptions={{
        lazy: true,
        onLazyLoad: onLazyLoad,
        itemSize: 38,
        showLoader: true,
        loading: loading,loadingTemplate: (options: any) => (
          <div style={{ height: '50px' }}>
            <Skeleton width={options.even ? '60%' : '50%'} height="1.3rem" />
          </div>
        ),
      }}
    />
  );
};

export const useInputDrivers = () => {
  const { listCompanyUsersDrivers } = useContext(ApiContext);
  const [drivers, setDrivers] = useState<LovDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(0);
  const pageSize = 30;

  const loadDrivers = async (page: number) => {
    if (loading || !hasMore) return;

    setLoading(true);
    listCompanyUsersDrivers(
      { pageNumber: page, pageSize: pageSize },
      '',
      (response) => {
        const newDrivers = response.data;
        setDrivers(prev => {
          const existingIds = new Set(prev.map(d => d.id));
          const uniqueNew = newDrivers.filter(d => !existingIds.has(d.id));
          return page === 0 ? newDrivers : [...prev, ...uniqueNew];
        });
        setHasMore(response.total > (page + 1) * pageSize);
        pageRef.current = page;
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    loadDrivers(0);
  }, []);

  const onLazyLoad = () => {
    if (hasMore && !loading) {
      loadDrivers(pageRef.current + 1);
    }
  };

  return { drivers, loading, onLazyLoad };
};